<script setup>
import { ref, onMounted, watch } from 'vue';
import apiFetch from '@wordpress/api-fetch';
import { useRouter } from 'vue-router';
import { setContactVerified, setUserEmail } from './utils/auth';
import { matomoTrackEvent } from './utils/matomo';
import RadioGroup from './components/RadioGroup.vue';
import { useUserStore } from './stores/user';

const router = useRouter();

const strings = ref(null);
const formConfig = ref(null);
const countries = ref([]);
const isLoading = ref(false);
const userStore = useUserStore();

const form = ref({
  name: '',
  organization: '',
  job_title: '',
  email: '',
  country: '',
  ecosystem_role: '',
  ebus_project: null,
  project_status: '',
  fleet_size: '',
  bus_type: '',
  charging_infrastructure: '',
  operational_context: '',
  timeline: '',
  financial_stage: '',
  additional_requirements: '',
  terms: false,
});

onMounted(async () => {
  const lang = document.documentElement.lang?.substring(0, 2);
  
  const [translationsData, formData, countriesData] = await Promise.all([
    apiFetch({ path: `/wp-json/ebus/translations?lang=${lang}` }),
    apiFetch({ path: `/wp-json/ebus/access-form?lang=${lang}` }),
    apiFetch({ path: `/wp-json/ebus/countries?all=1&lang=${lang}` })
  ]);

  strings.value = translationsData;
  formConfig.value = formData;
  countries.value = countriesData;
  form.value.email = userStore.email;
});

watch(isLoading, (value) => {
  document.body.style.overflow = value ? 'hidden' : '';
}, { immediate: true });

watch(() => form.value.ebus_project, (value) => {
  if (value !== true) {
    form.value.project_status = '';
    form.value.fleet_size = '';
    form.value.bus_type = '';
    form.value.charging_infrastructure = '';
    form.value.operational_context = '';
    form.value.timeline = '';
    form.value.financial_stage = '';
    form.value.additional_requirements = '';
  }
});

async function submitForm() {
  if (!form.value.name || !form.value.organization || !form.value.job_title || !form.value.email || !form.value.country || form.value.ebus_project === null || !form.value.terms || isLoading.value) {
    return;
  }

  matomoTrackEvent('contact-tool', 'ct_submit_registration');

  isLoading.value = true;

  const formData = {
    name: form.value.name,
    organization: form.value.organization,
    job_title: form.value.job_title,
    email: form.value.email,
    country: form.value.country,
    ecosystem_role: form.value.ecosystem_role,
    ebus_project: form.value.ebus_project,
    project_status: form.value.project_status,
    fleet_size: form.value.fleet_size,
    bus_type: form.value.bus_type,
    charging_infrastructure: form.value.charging_infrastructure,
    operational_context: form.value.operational_context,
    timeline: form.value.timeline,
    financial_stage: form.value.financial_stage,
    additional_requirements: form.value.additional_requirements,
    terms: form.value.terms,
  };

  try {
    await apiFetch({
      path: '/wp-json/ebus/create-contact',
      method: 'POST',
      data: formData,
    });

    setUserEmail(form.value.email);
    setContactVerified(false);
    router.push('/verification-code');
  } catch (error) {
    console.log('Error: ', error);
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <section v-if="strings && formConfig" class="relative! w-full! min-h-screen! flex! flex-col! gap-y-8! lg:gap-y-6! bg-light-aqua! px-6! pt-9! pb-25!">
    <div v-if="isLoading" class="fixed! inset-0! bg-gray/90! flex! items-center! justify-center! z-50!">
      <span class="w-20! h-20! border-4! border-aqua! border-t-transparent! rounded-full! animate-spin!"></span>
    </div>
    <div class="flex! flex-col! gap-y-8! lg:gap-y-3.5!">
      <h2 class="font-ibm-plex-mono! font-semibold! text-4xl! leading-tight! text-aqua!">
        <RouterLink class="text-aqua!" to="/">{{ strings.ebus_contacts_database }}</RouterLink>
      </h2>
      <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">
        <p>{{ strings.form_description }}</p>
        <p>{{ strings.mandatory_notice }}</p>
      </div>
    </div>

    <div class="w-full! h-full!">
      <h2 class="font-ibm-plex-mono! font-medium! text-28! leading-tight! text-aqua! mb-6! lg:mb-4.5!">{{ strings.access_form }}</h2>

      <form class="flex! flex-col! gap-y-8! lg:gap-y-4.5!" @submit.prevent="submitForm">
        <div class="flex! flex-col! gap-y-5! lg:gap-y-4!">
          <h3 class="font-ibm-plex-mono! font-medium! text-20! leading-tight! text-black!">1. {{ strings.identification }}</h3>

          <div class="flex! flex-col! gap-y-3!">
            <div class="flex! flex-col! gap-y-2!">
              <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">{{ strings.full_name }}*</div>
              <input class="w-full! lg:max-w-90.5! border-b! border-black! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!" v-model="form.name" required />
            </div>
            <div class="flex! flex-col! gap-y-2!">
              <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">{{ strings.organization }}*</div>
              <input class="w-full! lg:max-w-90.5! border-b! border-black! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!" v-model="form.organization" required />
            </div>
            <div class="flex! flex-col! gap-y-2!">
              <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">{{ strings.job_title }}*</div>
              <input class="w-full! lg:max-w-90.5! border-b! border-black! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!" v-model="form.job_title" required />
            </div>
            <div class="flex! flex-col! gap-y-2!">
              <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">{{ strings.email }}*</div>
              <input
                class="w-full! lg:max-w-90.5! border-b! border-black! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!"
                v-model="form.email"
                type="email"
                required
              />
            </div>
            <div class="flex! flex-col! gap-y-2!">
              <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!!">{{ strings.country }}*</div>
              <select class="w-full! lg:max-w-90.5! border-b! border-black! bg-transparent! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!" v-model="form.country" required>
                <option value="" disabled></option>
                <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="flex! flex-col! gap-y-5! lg:gap-y-4!">
          <h3 class="font-ibm-plex-mono! font-medium! text-20! leading-tight! text-black!">2. {{ strings.activity }}</h3>

          <RadioGroup
            :label="strings.ecosystem_role_question"
            :options="formConfig.ecosystem_role_options"
            name="ecosystem_role"
            v-model="form.ecosystem_role"
          />

          <RadioGroup
            :label="strings.ebus_project_question"
            :options="[
              { value: true, option: strings.yes },
              { value: false, option: strings.no }
            ]"
            name="ebus_project"
            v-model="form.ebus_project"
            :required="true"
          />
        </div>

        <div v-if="form.ebus_project === true" class="flex! flex-col! gap-y-5! lg:gap-y-4!">
          <h3 class="font-ibm-plex-mono! font-medium! text-20! leading-tight! text-black!">3. {{ strings.project_information }}</h3>

          <RadioGroup
            :label="strings.project_status"
            :options="formConfig.project_status_options"
            name="project_status"
            v-model="form.project_status"
          />

          <div class="w-full! lg:max-w-33! flex! flex-col! gap-y-1!">
            <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-aqua!">{{ strings.fleet_size }}</div>
            <input class="border-b! border-black! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!" v-model="form.fleet_size" />
          </div>

          <RadioGroup
            :label="strings.bus_type"
            :options="formConfig.bus_type_options"
            name="bus_type"
            v-model="form.bus_type"
          />

          <RadioGroup
            :label="strings.charging_infrastructure"
            :options="formConfig.charging_infrastructure_options"
            name="charging_infrastructure"
            v-model="form.charging_infrastructure"
          />

          <RadioGroup
            :label="strings.operational_context"
            :options="formConfig.operational_context_options"
            name="operational_context"
            v-model="form.operational_context"
          />

          <div class="w-full! lg:max-w-58! flex! flex-col! gap-y-1!">
            <div class="font-ibm-plex-mono! font-medium! text-base! leading-tight! text-aqua! mb-1.5!">{{ strings.timeline }}</div>
            <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">{{ strings.expected_deployment }}</div>
            <input class="border-b! border-black! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!" v-model="form.timeline" />
          </div>

          <RadioGroup
            :label="strings.financial_stage"
            :options="formConfig.financial_stage_options"
            name="financial_stage"
            v-model="form.financial_stage"
          />

          <div class="w-full! lg:max-w-82! flex! flex-col! gap-y-1.5!">
            <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-aqua!">{{ strings.additional_requirements }}</div>
            <textarea
              class="h-full! max-h-19! p-3! border! border-black! rounded-none! font-ibm-plex-mono! text-base! leading-tight! text-black! focus:outline-none!"
              v-model="form.additional_requirements"
            />
          </div>
        </div>

        <div class="flex! flex-col! gap-y-4!">
          <h3 class="font-ibm-plex-mono! font-medium! text-20! leading-tight! text-black!">4. {{ strings.terms_title }}</h3>
          <label class="flex! items-center! gap-x-2!">
            <input class="checkbox-input" type="checkbox" v-model="form.terms" required />
            <p class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! wysiwyg" v-html="strings.terms_agree"></p>
          </label>
        </div>

        <button
          class="self-start! font-ibm-plex-mono! font-normal! text-base! text-black! py-0.75! px-3.75! border! border-black! rounded-full! lg:mt-7! transition-all hover:text-white! hover:bg-aqua! disabled:opacity-60! disabled:cursor-not-allowed!"
          type="submit"
          :disabled="isLoading"
        >
          {{ strings.submit }}
        </button>
      </form>
    </div>
  </section>
</template>
